import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Dispatch, FC, Fragment, SetStateAction, useState } from 'react';

import { Piece, PieceAction } from '@apiContract';
import Footer from '@components/DrawerContent/Footer';
import Title from '@components/DrawerContent/Title';
import { useMailpiece } from '@hooks/useMailpiece';
import { MailPieceInboxType } from '@rdTypes/shared';

import { useMailpieceActions } from '../hooks/useMailpieceActions';
import { mailpieceActions } from '../index';
import { sxActionButton, sxActionIcon, sxActionItem, sxActionsList, sxDrawerPaper, sxP20 } from './style';

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  pieces: Piece[];
  access: MailPieceInboxType;
  pendingSearchValue?: string;
};

const ForceActionsDrawer: FC<Props> = ({ open, setOpen, pieces, access, pendingSearchValue }) => {
  const [selectedAction, setSelectedAction] = useState<PieceAction | null>(null);
  const [currentAction, setCurrentAction] = useState<PieceAction | null>(null);

  const { availableForceActions } = useMailpieceActions(pieces, access);
  const { getForceActionTitle } = useMailpiece();
  const forceActionTitle = getForceActionTitle(pieces);

  const handleClose = () => {
    setOpen(false);
    setSelectedAction(null);
    setCurrentAction(null);
  };

  const handleActionSelect = (action: PieceAction) => {
    setSelectedAction(selectedAction === action ? null : action);
  };

  const startAction = async () => {
    if (!selectedAction) return;
    setCurrentAction(selectedAction);
  };

  const handleExecute = () => {
    if (!selectedAction) return;

    const actionConfig = mailpieceActions[selectedAction];
    if (!actionConfig) return;

    void startAction();
  };

  const isSinglePiece = pieces.length === 1;

  const EmptyState = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        No force requests available
      </Typography>
    </Box>
  );

  const ActionItem = ({
    action,
    isAvailableForAllPieces,
  }: {
    action: PieceAction;
    isAvailableForAllPieces: boolean;
  }) => {
    const actionConfig = mailpieceActions[action];
    if (!actionConfig) return null;

    const { title, Icon, actionTriggerColor } = actionConfig;
    const isDisabled = !isSinglePiece && !isAvailableForAllPieces;
    const isSelected = selectedAction === action;
    const tooltipTitle = isDisabled ? 'Not available for some selected items' : '';

    return (
      <Fragment key={action}>
        <Box sx={{ mb: 1, mx: 1 }}>
          <Tooltip title={tooltipTitle} placement="bottom" disableHoverListener={!isDisabled} arrow>
            <ListItem disablePadding sx={sxActionItem(isSelected)}>
              <ListItemButton
                onClick={() => handleActionSelect(action)}
                disabled={isDisabled || currentAction !== null}
                sx={sxActionButton(actionTriggerColor, isDisabled)}
              >
                <ListItemIcon sx={sxActionIcon}>
                  <Icon color="inherit" />
                </ListItemIcon>

                <ListItemText primary={typeof title === 'function' ? title(pieces[0]) : title} />
              </ListItemButton>
            </ListItem>
          </Tooltip>
        </Box>

        {action === PieceAction.Delete && <Divider sx={{ my: 1, borderStyle: 'dashed' }} />}
      </Fragment>
    );
  };

  const ActionsList = () => (
    <Box sx={sxActionsList}>
      {availableForceActions.length === 0 ? (
        <EmptyState />
      ) : (
        <List disablePadding>
          {availableForceActions.map((actionData) => (
            <ActionItem key={actionData.action} {...actionData} />
          ))}
        </List>
      )}
    </Box>
  );

  return (
    <>
      <Drawer open={open} anchor="right" onClose={handleClose} slotProps={{ paper: sxDrawerPaper }}>
        <Title title={forceActionTitle} onClose={handleClose} />
        <ActionsList />
        <Footer
          sx={sxP20}
          disabled={!selectedAction || currentAction !== null}
          title={forceActionTitle}
          handleSubmit={handleExecute}
        />
      </Drawer>

      {availableForceActions.map(({ action }) => {
        const actionConfig = mailpieceActions[action];
        if (!actionConfig?.Component) return null;

        const { Component } = actionConfig;

        return (
          <Component
            key={action}
            mailpieces={pieces}
            actionStarted={currentAction === action}
            onActionFinish={() => {
              setCurrentAction(null);
              handleClose();
            }}
            pendingSearchValue={pendingSearchValue}
            isForce
          />
        );
      })}
    </>
  );
};

export default ForceActionsDrawer;
